import styled from 'styled-components';

import { COLORS, MOBILE } from '../../theme/tokens';
import { lightPanelShadow } from '../../theme/mixins';
import { Panel } from '../Panel/Panel';
import { Text } from '../Text/Text';
import { GaugeScore } from '../ScoreGauge/ScoreGauge.styles';

// The panel measures its OWN width (inline-size container `insights`), so it reflows
// when the content card is squeezed by the Lumio panel even though the viewport stays
// wide. TIGHT trims the gaps + tile padding so three sub-gauges keep fitting; STACK
// drops the right-hand block below the gauge + headline (same shape as the MOBILE
// viewport stack, which still applies on real phones).
const TIGHT = '@container insights (max-width: 760px)';
const STACK = '@container insights (max-width: 640px)';

export const Root = styled(Panel)<{ $glow: string }>`
  /* Light theme: Home's score-card shadow at a third of its opacity (the user, 2026-10-22). */
  ${lightPanelShadow}
  container: insights / inline-size;
  padding: 26px 30px;
  /* Radial glow whose hue tracks the main gauge's grade (see heroGlowForScore). */
  background: ${(p) => p.$glow};
  ${MOBILE} {
    padding: 10px;
  }
`;

export const Body = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  ${TIGHT} {
    gap: 18px;
  }
  ${STACK} {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
  }
  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
  }
`;

export const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  flex: 0 0 auto;
  max-width: 46%;
  /* The gauge has a fixed size; keep its (shrinkable) Reveal wrapper from being
     squeezed by the flex row, or the headline slides underneath the ring. */
  & > *:first-child {
    flex: 0 0 auto;
  }
  ${TIGHT} {
    gap: 16px;
    max-width: 42%;
  }
  ${STACK} {
    max-width: none;
  }
  ${MOBILE} {
    max-width: none;
  }
`;

export const LeftText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const Title = styled(Text)`
  font-size: 18px;
  font-weight: 400;
  color: ${COLORS.textBright};
  line-height: 1.35;
  ${TIGHT} {
    font-size: 16px;
  }
`;

// --- Recommendations variant ------------------------------------------------
export const RecoCard = styled.div`
  flex: 1;
  align-self: stretch;
  background: ${COLORS.overlaySoft};
  border-radius: 16px;
  padding: 4px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${TIGHT} {
    padding: 4px 16px;
  }
`;

export const RecoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 2px;
  cursor: pointer;
  & + & {
    border-top: 1px solid ${COLORS.hairline};
  }
`;

export const RecoLabel = styled(Text)`
  flex: 1;
  font-size: 16px;
  color: ${COLORS.textBright};
`;

// --- Metrics (gauge row) variant --------------------------------------------
export const MetricGauges = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;
  /* Equal-width translucent tiles filling the row. 12px between them: the 5px the
     Figma frame had was tuned for opaque tiles, and once they went translucent the
     seam between two neighbours stopped reading as a gap at all. */
  gap: 12px;
`;

export const MetricGauge = styled.div`
  flex: 1;
  /* Let the tiles shrink to share the row instead of forcing their 76px gauge +
     padding min-width (which overflowed the narrow mobile card). */
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  /* Translucent tile behind each metric sub-gauge (Figma: black @ 0.3). */
  background: ${COLORS.insightsTileBg};
  border-radius: 22px;
  padding: 20px;
  /* Squeezed row (Lumio open): trim the tile padding so the three fixed-size gauges
     keep clear of the tile edges instead of bleeding past them. */
  ${TIGHT} {
    padding: 14px 8px;
    gap: 6px;
  }

  /* Secondary sub-gauge numbers read lighter than the primary gauge's score. */
  ${GaugeScore} {
    font-weight: 400;
  }
`;

export const MetricLabel = styled(Text)`
  font-size: 13px;
  color: ${COLORS.textMuted};
`;
